/** Batch processing: one setting applied to several files, one daily use for the whole batch. */

/** Files per batch on the trial; paid accounts are not limited by count. */
export const TRIAL_BATCH_LIMIT = 5;
/** A practical ceiling for everyone: every file is held in memory while the batch runs. */
export const BATCH_HARD_CAP = 100;

export function batchLimit(opts: { isPro: boolean; isAdmin: boolean; isLoading: boolean }): number {
  // While the plan is still loading, assume the trial limit; the list can grow once it arrives.
  if (opts.isLoading) return TRIAL_BATCH_LIMIT;
  return opts.isPro || opts.isAdmin ? BATCH_HARD_CAP : TRIAL_BATCH_LIMIT;
}

export function addToBatch<T>(current: T[], incoming: T[], limit: number): { files: T[]; dropped: number } {
  const room = Math.max(0, limit - current.length);
  return { files: [...current, ...incoming.slice(0, room)], dropped: Math.max(0, incoming.length - room) };
}

export type BatchStatus = "queued" | "working" | "done" | "failed" | "skipped";

export interface BatchItem {
  id: number;
  file: File;
  status: BatchStatus;
  result?: { blob: Blob; fileName: string };
  error?: string;
}

/** Names inside the ZIP: unique even when two inputs share a name. */
export function uniqueZipNames(names: string[]): string[] {
  const used = new Set<string>();
  return names.map((n) => {
    const dot = n.lastIndexOf(".");
    const base = dot > 0 ? n.slice(0, dot) : n;
    const ext = dot > 0 ? n.slice(dot) : "";
    let out = n, i = 1;
    while (used.has(out.toLowerCase())) { i++; out = `${base} (${i})${ext}`; }
    used.add(out.toLowerCase());
    return out;
  });
}

/** Settles as soon as the signal aborts, whether or not the work underneath notices. */
function raceAbort<T>(work: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return work;
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(new DOMException("Stopped", "AbortError"));
    if (signal.aborted) return onAbort();
    signal.addEventListener("abort", onAbort, { once: true });
    work.then(resolve, reject).finally(() => signal.removeEventListener("abort", onAbort));
  });
}

/**
 * Runs the files one after another so memory stays low and one bad file cannot stop the rest. A failure is
 * recorded on that file only. After `shouldStop()` turns true, files not yet started are marked skipped.
 */
export async function runBatch(
  items: BatchItem[],
  process: (file: File, signal?: AbortSignal) => Promise<{ blob: Blob; fileName: string }>,
  onUpdate: (items: BatchItem[]) => void,
  shouldStop: () => boolean = () => false,
  /** Aborting it ends the file being worked on at once (even if the tool does not watch the signal) and skips the rest. */
  signal?: AbortSignal,
): Promise<BatchItem[]> {
  let state: BatchItem[] = items.map((i) => ({ id: i.id, file: i.file, status: "queued" }));
  const push = (idx: number, patch: Partial<BatchItem>) => {
    state = state.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onUpdate(state);
  };
  onUpdate(state);
  for (let i = 0; i < state.length; i++) {
    if (shouldStop()) { push(i, { status: "skipped" }); continue; }
    push(i, { status: "working" });
    try {
      const result = await raceAbort(process(state[i].file, signal), signal);
      push(i, { status: "done", result });
    } catch (e) {
      if (signal?.aborted || (e as Error)?.name === "AbortError") { push(i, { status: "skipped" }); continue; }
      push(i, { status: "failed", error: e instanceof Error ? e.message : "This file could not be processed." });
    }
  }
  return state;
}

export function batchTotals(items: BatchItem[]) {
  const done = items.filter((i) => i.status === "done" && i.result);
  return {
    done: done.length,
    failed: items.filter((i) => i.status === "failed").length,
    before: done.reduce((s, i) => s + i.file.size, 0),
    after: done.reduce((s, i) => s + (i.result?.blob.size ?? 0), 0),
  };
}
